import React, { useRef } from 'react'
import {
  Autocomplete,
  Box,
  Grid,
  IconButton,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Switch,
  Tooltip,
  Alert,
} from '@mui/material'
import { Music, Video, X, Upload, Lock, Users } from 'lucide-react'
import { PostContentProps } from 'components/PostScheduling/PostContent'
import { Platform, useMediaAccountsQuery } from 'graphql/generated/graphql'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import MUICaptionWithEmojis from '../../MUICaptionWithEmojis'

interface MediaFile {
  file: File
}

function SortableMediaItem({ id, children }: { id: string; children: React.ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })
  const style = { 
    transform: CSS.Transform.toString(transform), 
    transition, 
    cursor: 'grab',
    opacity: isDragging ? 0.5 : 1,
  }
  
  return (
    <Box sx={{ position: 'relative' }}>
      {/* Invisible drag handle */}
      <Box
        ref={setNodeRef}
        {...attributes}
        {...listeners}
        sx={{
          ...style,
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1,
        }}
      />
      {/* Content */}
      <Box sx={{ position: 'relative', zIndex: 2 }}>
        {children}
      </Box>
    </Box>
  )
}

export default function ModernTikTokPostContent({
  videoFiles,
  setVideoFiles,
  setProfileValue,
  profileValue,
  captionRef,
  tiktokAdditionalData,
  setTiktokAdditionalData,
}: PostContentProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // FIXED: Prevents accidental drags
      },
    })
  )
  const [switchValue, setSwitchValue] = React.useState(false)
  const [tooltipOpen, setTooltipOpen] = React.useState(false)

  const { data, loading } = useMediaAccountsQuery({ variables: { platform: Platform.TikTok } })

  const handleFiles = (newFiles: FileList | null, maxFiles = 10, maxFileSizeMB = 500) => {
    if (!newFiles) return
    const validFiles: MediaFile[] = Array.from(newFiles)
      .filter((file) => {
        if (!['video/mp4', 'video/mov', 'video/quicktime'].includes(file.type)) {
          alert(`Unsupported file type: ${file.type}`)
          return false
        }
        if (file.size > maxFileSizeMB * 1024 * 1024) {
          alert(`${file.name} exceeds ${maxFileSizeMB}MB`)
          return false
        }
        return true
      })
      .map((f) => ({ file: f }))
    if (videoFiles.length + validFiles.length > maxFiles) {
      alert(`Max ${maxFiles} videos allowed.`)
      return
    }
    setVideoFiles([...videoFiles, ...validFiles.map(v => v.file)])
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = videoFiles.findIndex(
      (v) => `${String(v.name)}-${String(v.lastModified)}` === active.id
    )
    const newIndex = videoFiles.findIndex(
      (v) => `${String(v.name)}-${String(v.lastModified)}` === over.id
    )

    if (oldIndex !== -1 && newIndex !== -1) {
      setVideoFiles(arrayMove(videoFiles, oldIndex, newIndex))
    }
  }

  const handleRemove = (index: number) => {
    const newList = [...videoFiles]
    newList.splice(index, 1)
    setVideoFiles(newList)
  }

  return (
    <Box sx={{ color: '#fff' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #00f2ea 0%, #ff0050 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Music size={24} color="#fff" />
        </Box>
        <Box>
          <Typography sx={{ fontSize: 24, fontWeight: 600, color: '#fff' }}>TikTok Video</Typography>
          <Typography sx={{ fontSize: 14, color: 'rgba(255,255,255,0.6)' }}>
            Create viral content for TikTok
          </Typography>
        </Box>
      </Box>

      {/* Account Selection */}
      <Box sx={{ mb: 3, p: 3, borderRadius: 2, border: '1px solid rgba(168,85,247,0.15)', background: 'rgba(30,41,59,0.4)' }}>
        <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 2, color: '#fff' }}>Select Account</Typography>
        <Autocomplete
          loading={loading}
          options={data?.mediaAccounts ?? []}
          getOptionLabel={(option) => option.nickname ?? option.accountName}
          onChange={(e, value) => value && setProfileValue(value)}
          renderInput={(params) => (
            <TextField 
              {...params} 
              label="Choose your TikTok account"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: 'rgba(168, 85, 247, 0.3)' },
                  '&:hover fieldset': { borderColor: 'rgba(168, 85, 247, 0.5)' },
                  '&.Mui-focused fieldset': { borderColor: '#a855f7' },
                  backgroundColor: 'rgba(15, 23, 42, 0.5)',
                },
                '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                '& .MuiInputBase-input': { color: '#fff' },
              }}
            />
          )}
          sx={{
            '& .MuiAutocomplete-endAdornment': {
              '& .MuiSvgIcon-root': { color: 'rgba(255, 255, 255, 0.7)' },
            },
          }}
        />
        {data?.mediaAccounts.length === 0 && !loading && (
          <Box sx={{ mt: 2, p: 1.5, borderRadius: 1, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#fca5a5', fontSize: 14 }}>
            ⚠️ Please connect your TikTok account first.
          </Box>
        )}
      </Box>

      {/* Video Upload & Preview */}
      <Box
        onClick={() => inputRef.current?.click()}
        sx={{
          mb: 3,
          p: 3,
          borderRadius: 2,
          border: '2px dashed rgba(168,85,247,0.4)',
          cursor: 'pointer',
          textAlign: 'center',
          background: 'rgba(15, 23, 42, 0.5)',
          '&:hover': {
            borderColor: 'rgba(168, 85, 247, 0.7)',
            background: 'rgba(31, 41, 55, 0.6)',
          },
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept="video/*"
          multiple
          style={{ display: 'none' }}
          onChange={(e) => handleFiles(e.target.files)}
        />
        <Upload size={28} color="#a855f7" />
        <Typography sx={{ color: '#a855f7', mt: 1, fontWeight: 500 }}>
          Upload Videos
        </Typography>

        {videoFiles.length > 0 && (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={videoFiles.map(
                (v) => `${String(v.name)}-${String(v.lastModified)}`
              )}
              strategy={verticalListSortingStrategy}
            >
              <Grid container spacing={1.5} sx={{ mt: 2, justifyContent: 'center' }}>
                {videoFiles.map((v, index) => (
                  <Grid item key={`${String(v.name)}-${String(v.lastModified)}`}>
                    <SortableMediaItem
                      id={`${String(v.name)}-${String(v.lastModified)}`}
                    >
                      <Box
                        sx={{
                          position: 'relative',
                          width: 200,
                          height: 200,
                          borderRadius: 2,
                          overflow: 'hidden',
                          border: '2px solid rgba(168,85,247,0.3)',
                        }}
                      >
                        <video
                          src={URL.createObjectURL(v)}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                          muted
                          loop
                        />
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 4,
                            left: 4,
                            background: 'rgba(0,0,0,0.7)',
                            color: '#fff',
                            px: 1,
                            borderRadius: 1,
                            fontSize: 12,
                            fontWeight: 600,
                            zIndex: 3,
                          }}
                        >
                          {index + 1}
                        </Box>
                        <IconButton
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            handleRemove(index)
                          }}
                          sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            background: 'rgba(0,0,0,0.7)',
                            '&:hover': { 
                              background: 'rgba(239, 68, 68, 0.9)',
                              transform: 'scale(1.1)',
                            },
                            width: 28,
                            height: 28,
                            zIndex: 10, // FIXED: Higher z-index
                            transition: 'all 0.2s ease',
                          }}
                        >
                          <X size={16} color="#fff" />
                        </IconButton>
                      </Box>
                    </SortableMediaItem>
                  </Grid>
                ))}
              </Grid>
            </SortableContext>
          </DndContext>
        )}
      </Box>

        {/* Caption with Emoji Support */}
        <MUICaptionWithEmojis
          captionRef={captionRef}
          placeholder="Write your caption... Don't forget the hashtags! #Instagram"
          maxLength={2200}
          rows={5}
        />

      {/* TikTok Settings - Only show if profile selected */}
      {profileValue && (
        <>
          {/* Privacy Settings */}
          <Box sx={{ mb: 3, p: 3, borderRadius: 2, background: 'rgba(30,41,59,0.4)', border: '1px solid rgba(168,85,247,0.15)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Lock size={20} color="#a855f7" />
              <Typography sx={{ fontSize: 16, fontWeight: 600, color: '#fff' }}>Who can view this video?</Typography>
            </Box>
            <FormControl fullWidth>
              <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)', '&.Mui-focused': { color: '#a855f7' } }}>
                Privacy Level
              </InputLabel>
              <Select
                value={tiktokAdditionalData?.privacy_level}
                label="Privacy Level"
                onChange={(e) => {
                  if (e.target.value === 'SELF_ONLY') setSwitchValue(false)
                  setTiktokAdditionalData?.((prev) => ({ ...prev, privacy_level: e.target.value }))
                }}
                sx={{
                  color: '#fff',
                  backgroundColor: 'rgba(15,23,42,0.5)',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(168,85,247,0.3)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(168,85,247,0.5)' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#a855f7' },
                  '& .MuiSvgIcon-root': { color: 'rgba(255, 255, 255, 0.7)' },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      bgcolor: 'rgba(30, 41, 59, 0.95)',
                      backdropFilter: 'blur(20px)',
                      border: '1px solid rgba(168, 85, 247, 0.2)',
                      color: '#fff',
                      '& .MuiMenuItem-root': {
                        color: '#fff',
                        '&:hover': { bgcolor: 'rgba(168, 85, 247, 0.1)' },
                        '&.Mui-selected': { 
                          bgcolor: 'rgba(168, 85, 247, 0.2)',
                          '&:hover': { bgcolor: 'rgba(168, 85, 247, 0.3)' },
                        },
                      },
                    },
                  },
                }}
              >
                {profileValue?.privacy_level_options?.map((item) => (
                  <MenuItem key={item} value={item}>{item.replace(/_/g, ' ')}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* User Permissions */}
          <Box sx={{ mb: 3, p: 3, borderRadius: 2, background: 'rgba(30,41,59,0.4)', border: '1px solid rgba(168,85,247,0.15)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Users size={20} color="#a855f7" />
              <Typography sx={{ fontSize: 16, fontWeight: 600, color: '#fff' }}>Allow users to</Typography>
            </Box>
            <FormGroup>
              <FormControlLabel
                disabled={Boolean(profileValue?.comments_disabled)}
                control={
                  <Checkbox
                    onChange={(e) => setTiktokAdditionalData?.((prev) => ({ ...prev, allow_comments: e.target.checked }))}
                    sx={{
                      color: 'rgba(168, 85, 247, 0.5)',
                      '&.Mui-checked': { color: '#a855f7' },
                      '&.Mui-disabled': { color: 'rgba(255, 255, 255, 0.3)' },
                    }}
                  />
                }
                label="Comment"
                sx={{ '.MuiFormControlLabel-label': { color: '#fff', fontSize: 15, '&.Mui-disabled': { color: 'rgba(255, 255, 255, 0.5)' } }, mb: 1 }}
              />
              <FormControlLabel
                disabled={Boolean(profileValue?.duet_disabled)}
                control={
                  <Checkbox
                    onChange={(e) => setTiktokAdditionalData?.((prev) => ({ ...prev, allow_duet: e.target.checked }))}
                    sx={{
                      color: 'rgba(168, 85, 247, 0.5)',
                      '&.Mui-checked': { color: '#a855f7' },
                      '&.Mui-disabled': { color: 'rgba(255, 255, 255, 0.3)' },
                    }}
                  />
                }
                label="Duet"
                sx={{ '.MuiFormControlLabel-label': { color: '#fff', fontSize: 15, '&.Mui-disabled': { color: 'rgba(255, 255, 255, 0.5)' } }, mb: 1 }}
              />
              <FormControlLabel
                disabled={Boolean(profileValue?.stitch_disabled)}
                control={
                  <Checkbox
                    onChange={(e) => setTiktokAdditionalData?.((prev) => ({ ...prev, allow_stitch: e.target.checked }))}
                    sx={{
                      color: 'rgba(168, 85, 247, 0.5)',
                      '&.Mui-checked': { color: '#a855f7' },
                      '&.Mui-disabled': { color: 'rgba(255, 255, 255, 0.3)' },
                    }}
                  />
                }
                label="Stitch"
                sx={{ '.MuiFormControlLabel-label': { color: '#fff', fontSize: 15, '&.Mui-disabled': { color: 'rgba(255, 255, 255, 0.5)' } } }}
              />
            </FormGroup>
          </Box>

          {/* Branded Content */}
          {tiktokAdditionalData?.privacy_level && (
            <Box sx={{ p: 3, borderRadius: 2, background: 'rgba(30,41,59,0.4)', border: '1px solid rgba(168,85,247,0.15)' }}>
              <Tooltip title="Branded content videos cannot be set to private." placement="bottom-start" open={tooltipOpen}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: switchValue ? 3 : 0 }}
                  onMouseEnter={() => { if (tiktokAdditionalData.privacy_level === 'SELF_ONLY') setTooltipOpen(true) }}
                  onMouseLeave={() => setTooltipOpen(false)}
                >
                  <Typography sx={{ fontSize: 16, fontWeight: 600, color: '#fff' }}>Disclose video content</Typography>
                  <Switch
                    disabled={tiktokAdditionalData.privacy_level === 'SELF_ONLY'}
                    checked={switchValue}
                    onChange={(e) => {
                      if (!e.target.checked) setTiktokAdditionalData?.((prev) => ({ ...prev, brand_content: false, brand_organic: false }))
                      setTiktokAdditionalData?.((prev) => ({ ...prev, brand_content: e.target.checked }))
                      setSwitchValue(e.target.checked)
                    }}
                    sx={{
                      '& .MuiSwitch-switchBase': { color: 'rgba(148, 163, 184, 0.5)' },
                      '& .MuiSwitch-track': { backgroundColor: 'rgba(71, 85, 105, 0.5)', opacity: 1 },
                      '& .MuiSwitch-switchBase.Mui-checked': { color: '#a855f7' },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: 'rgba(168, 85, 247, 0.5)', opacity: 1 },
                    }}
                  />
                </Box>
              </Tooltip>

              {switchValue && (
                <Box>
                  {!tiktokAdditionalData?.brand_content && !tiktokAdditionalData?.brand_organic && (
                    <Alert severity="warning" sx={{ mb: 2, backgroundColor: 'rgba(245, 158, 11, 0.1)', border: '1px solid rgba(245, 158, 11, 0.3)', color: '#fbbf24', '& .MuiAlert-icon': { color: '#fbbf24' } }}>
                      You need to indicate if your content promotes yourself, a third party, or both.
                    </Alert>
                  )}
                  {tiktokAdditionalData?.brand_content && !tiktokAdditionalData.brand_organic && (
                    <Alert severity="info" sx={{ mb: 2, backgroundColor: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.3)', color: '#93c5fd', '& .MuiAlert-icon': { color: '#93c5fd' } }}>
                      Your video will be labeled as &apos;Promotional content&apos;. This cannot be changed once your video is posted.
                    </Alert>
                  )}
                  {tiktokAdditionalData?.brand_organic && (
                    <Alert severity="info" sx={{ mb: 2, backgroundColor: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.3)', color: '#93c5fd', '& .MuiAlert-icon': { color: '#93c5fd' } }}>
                      Your video will be labeled as &apos;Paid partnership&apos;. This cannot be changed once your video is posted.
                    </Alert>
                  )}

                  <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, mb: 2, lineHeight: 1.5 }}>
                    Turn on to disclose that this video promotes goods or services in exchange for something of value.
                  </Typography>

                  <Box sx={{ background: 'rgba(15, 23, 42, 0.5)', borderRadius: 2, p: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 600, flex: 1, color: '#fff' }}>Your brand</Typography>
                      <Checkbox
                        checked={tiktokAdditionalData?.brand_content}
                        onChange={(e) => setTiktokAdditionalData?.((prev) => ({ ...prev, brand_content: e.target.checked }))}
                        sx={{ color: 'rgba(168, 85, 247, 0.5)', '&.Mui-checked': { color: '#a855f7' } }}
                      />
                    </Box>
                    <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, m: 0 }}>
                      You are promoting yourself or your own business.
                    </Typography>
                  </Box>

                  <Box sx={{ background: 'rgba(15, 23, 42, 0.5)', borderRadius: 2, p: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 600, flex: 1, color: '#fff' }}>Branded content</Typography>
                      <Checkbox
                        checked={tiktokAdditionalData?.brand_organic}
                        onChange={(e) => setTiktokAdditionalData?.((prev) => ({ ...prev, brand_organic: e.target.checked }))}
                        sx={{ color: 'rgba(168, 85, 247, 0.5)', '&.Mui-checked': { color: '#a855f7' } }}
                      />
                    </Box>
                    <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, m: 0 }}>
                      You are promoting another brand or a third party.
                    </Typography>
                  </Box>

                  {tiktokAdditionalData?.brand_content && !tiktokAdditionalData.brand_organic && (
                    <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 13, m: 0 }}>
                      By posting, you agree to TikTok&apos;s{' '}
                      <a href="https://www.tiktok.com/legal/page/global/music-usage-confirmation/en" target="_blank" rel="noreferrer" style={{ color: '#a855f7', textDecoration: 'none' }}>
                        Music Usage Confirmation
                      </a>.
                    </Typography>
                  )}
                  {tiktokAdditionalData?.brand_organic && (
                    <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 13, m: 0 }}>
                      By posting, you agree to TikTok&apos;s{' '}
                      <a href="https://www.tiktok.com/legal/page/global/bc-policy/en" target="_blank" rel="noreferrer" style={{ color: '#a855f7', textDecoration: 'none' }}>
                        Branded Content Policy
                      </a>{' '}
                      and{' '}
                      <a href="https://www.tiktok.com/legal/page/global/music-usage-confirmation/en" target="_blank" rel="noreferrer" style={{ color: '#a855f7', textDecoration: 'none' }}>
                        Music Usage Confirmation
                      </a>.
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          )}
        </>
      )}
    </Box>
  )
}